import { useEffect, useRef, useState } from "react";
import { Box, Alert, Button, IconButton, Tooltip } from "@mui/material";
import {
  Info as InfoIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { getCoinOptions } from "../../../utils/exchangeManager";
import ChangelingMonetaryInput from "../ChangelingMonetaryInput";
import { LabeledInput } from "../LabeledInput";

export const CUSTOM_EXCHANGE_MESSAGE =
  "Este valor de câmbio foi personalizado para esta categoria e não será atualizado automaticamente caso o câmbio padrão do evento seja alterado.";

interface ExchangeFieldsProps {
  date: string;
  currency: string;
  value: number;
  onDateChange?: (date: string) => void;
  // `date` só é informado no modo gatedValueEdit, onde data, moeda e valor são
  // confirmados juntos. Os demais consumidores continuam recebendo dois args.
  onExchangeChange: (currency: string, value: number, date?: string) => void;
  disabled?: boolean;
  dateLabel?: string;
  valueLabel?: string;
  dateError?: boolean;
  dateHelperText?: string;
  valueHelperText?: string;
  showTitle?: boolean;
  titleStyle?: React.CSSProperties;
  // Novos props para Cenário 3
  eventCurrency?: string;
  disableDateAndValue?: boolean;
  showSyncMessage?: boolean;
  onNavigateToEvent?: () => void;
  // Permite editar data/valor mesmo quando a moeda é igual à do evento
  // (usado em negociação/fechamento para sobrescrever manualmente o câmbio).
  allowSameCurrencyOverride?: boolean;
  // Trava só o seletor de moeda (ex.: câmbio por serviço no modal de taxas,
  // onde a moeda segue a categoria e só data/cotação mudam).
  disableCurrency?: boolean;
  // Modo "câmbio por categoria": data, moeda e valor nascem bloqueados e abrem
  // juntos pelo ícone de editar. O que for alterado só é propagado ao confirmar
  // — cancelar descarta o rascunho.
  gatedValueEdit?: boolean;
  // Indica que a categoria já possui um câmbio personalizado (exibe o aviso).
  isCustom?: boolean;
}

/* eslint-disable react/prop-types */
const ExchangeFields: React.FC<ExchangeFieldsProps> = ({
  date,
  currency,
  value,
  onDateChange,
  onExchangeChange,
  disabled = false,
  dateLabel = "Data cotação",
  valueLabel = "Moeda / valor dia em R$",
  dateError = false,
  dateHelperText,
  valueHelperText,
  showTitle = false,
  titleStyle = { fontWeight: 600, marginBottom: 8 },
  eventCurrency,
  disableDateAndValue = false,
  showSyncMessage = false,
  onNavigateToEvent,
  allowSameCurrencyOverride = false,
  disableCurrency = false,
  gatedValueEdit = false,
  isCustom = false,
}) => {
  const coinOptions = getCoinOptions();

  const [isEditingValue, setIsEditingValue] = useState(false);
  // Rascunho de data + moeda + valor enquanto a edição está aberta. O ref
  // espelha o state para que "Confirmar" leia o último rascunho mesmo que o
  // blur do input aconteça no mesmo gesto do clique.
  const [draft, setDraft] = useState({ date, currency, value });
  const draftRef = useRef({ date, currency, value });

  const writeDraft = (next: Partial<typeof draftRef.current>) => {
    draftRef.current = { ...draftRef.current, ...next };
    setDraft(draftRef.current);
  };

  useEffect(() => {
    if (!isEditingValue) {
      draftRef.current = { date, currency, value };
      setDraft(draftRef.current);
    }
  }, [date, currency, value, isEditingValue]);

  const updateCalcValues = (
    type: string,
    newValue: number,
    calcType: string
  ) => {
    if (gatedValueEdit) {
      // Segura moeda e valor em rascunho: só vão para o contexto no "Confirmar".
      // A regra do BRL vem do próprio ChangelingMonetaryInput, que força valor 1
      // e desabilita o campo — aqui só registramos o que ele emitiu.
      if (isEditingValue) {
        writeDraft({ currency: calcType, value: newValue });
      }
      return;
    }
    onExchangeChange(calcType, newValue);
  };

  const handleDateChange = (newDate: string) => {
    if (gatedValueEdit) {
      if (isEditingValue) {
        writeDraft({ date: newDate });
      }
      return;
    }
    onDateChange?.(newDate);
  };

  function startValueEdit() {
    writeDraft({ date, currency, value });
    setIsEditingValue(true);
  }

  function cancelValueEdit() {
    setIsEditingValue(false);
    writeDraft({ date, currency, value });
  }

  function confirmValueEdit() {
    setIsEditingValue(false);
    const confirmed = draftRef.current;
    if (
      confirmed.currency !== currency ||
      confirmed.value !== value ||
      confirmed.date !== date
    ) {
      onExchangeChange(confirmed.currency, confirmed.value, confirmed.date);
    }
  }

  // Detecta se a moeda da categoria é igual à do evento
  const isSameCurrency = Boolean(eventCurrency && currency === eventCurrency);
  const shouldDisableDateAndValue =
    disableDateAndValue || (isSameCurrency && !allowSameCurrencyOverride);

  // No modo por categoria os três campos abrem juntos pelo ícone de editar e
  // voltam a travar ao confirmar/cancelar. No câmbio por serviço a moeda
  // continua travada via disableCurrency.
  const isDateDisabled = gatedValueEdit
    ? disabled || !isEditingValue
    : disabled || shouldDisableDateAndValue;
  const isValueDisabled = gatedValueEdit
    ? disabled || !isEditingValue
    : shouldDisableDateAndValue;
  const isCurrencyDisabled =
    disableCurrency || (gatedValueEdit && !isEditingValue);
  const displayDate = gatedValueEdit ? draft.date : date;
  const displayValue = gatedValueEdit ? draft.value : value;
  const displayCurrency = gatedValueEdit ? draft.currency : currency;

  // Enquanto a categoria segue o padrão do evento, reforça de onde vem o câmbio.
  const shouldShowSyncMessage = gatedValueEdit
    ? showSyncMessage && !isCustom && !isEditingValue
    : showSyncMessage && isSameCurrency;

  return (
    <>
      {showTitle && <span style={titleStyle}>Câmbio</span>}

      {/* Mensagem informativa quando moeda é igual */}
      {shouldShowSyncMessage && (
        <Box mb={2}>
          <Alert
            severity="info"
            icon={<InfoIcon />}
            action={
              onNavigateToEvent && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={onNavigateToEvent}
                >
                  Ir para Dados Cadastrais
                </Button>
              )
            }
          >
            A data e o valor seguem o câmbio definido em Dados Cadastrais do
            Evento.
          </Alert>
        </Box>
      )}

      <Box display={"flex"} gap={3} mt={showTitle ? 1 : 0}>
        <LabeledInput
          type="date"
          label={dateLabel}
          value={displayDate}
          onChange={(e) => handleDateChange(e.target.value)}
          disabled={isDateDisabled}
          error={dateError}
          helperText={dateHelperText}
        />
        <ChangelingMonetaryInput
          prevValue={{
            calcType: displayCurrency || "BRL",
            value: displayValue || 1,
          }}
          disabled={disabled}
          disableValue={isValueDisabled}
          disableCurrency={isCurrencyDisabled}
          label={valueLabel}
          type={""}
          calculusOptions={coinOptions}
          updateCalcValues={updateCalcValues}
          helperText={valueHelperText}
          foreignDecimalScale={10}
        />
        {/* Alinha os botões pela base do campo de valor (os inputs têm label acima e my={1}). */}
        {gatedValueEdit && !disabled && (
          <Box
            display="flex"
            alignItems="flex-end"
            gap={1}
            flexShrink={0}
            my={1}
          >
            {isEditingValue ? (
              <>
                <Button
                  onClick={cancelValueEdit}
                  variant="outlined"
                  color="inherit"
                  startIcon={<CloseIcon />}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmValueEdit}
                  variant="contained"
                  startIcon={<CheckIcon />}
                >
                  Confirmar
                </Button>
              </>
            ) : (
              <Tooltip title="Personalizar câmbio desta categoria">
                <IconButton
                  onClick={startValueEdit}
                  size="small"
                  color="primary"
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {gatedValueEdit && isCustom && !isEditingValue && (
        <Box mt={2}>
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{
              backgroundColor: "#F4EAFB",
              color: "#4A148C",
              "& .MuiAlert-icon": { color: "secondary.main" },
            }}
          >
            {CUSTOM_EXCHANGE_MESSAGE}
          </Alert>
        </Box>
      )}
    </>
  );
};

export default ExchangeFields;
